import PropTypes from "prop-types";
import DownloadingIcon from "@mui/icons-material/Downloading";
import { Button, Tooltip } from "@mui/material";
import { isAwardedBid, isDeletedBid } from "../../util/bid/bidCommon";
import { parseBalancesCSV, validateBalancesCSV } from "../../util/bid/ftoBidUtils";
import { useState } from "react";
import WarningDialog from "../fundamentals/WarningDialog.jsx";
import SuccessDialog from "../fundamentals/SuccessDialog";
import {
  TWU_DAT_FTO_BALANCES_IMPORT_ERROR_MESSAGE,
  IAM_FTO_BALANCES_IMPORT_ERROR_MESSAGE,
  IAM_DAT_BALANCES_IMPORT_ERROR_MESSAGE,
  NC_PTO_FTO_BALANCES_IMPORT_ERROR_MESSAGE,
  NC_PTO_GOC_HOL_BALANCES_IMPORT_ERROR_MESSAGE,
} from "../../util/bid/messageConstants";
import { BID_TYPE } from "../../util/bid/bidConstants";
import { parseTWUDATBalancesCSV, validateTWUDATBalancesCSV } from "../../util/bid/TWUDATFTOBidUtils";
import { parseIAMDATBalancesCSV, validateIAMDATBalancesCSV } from "../../util/bid/IAMDATBidUtils";
import {
  parseNCPTOFTOBalancesCSV,
  parseNCPTOGOCHOLBalancesCSV,
  validateNCPTOFTOBalancesCSV,
  validateNCPTOGOCHOLBalancesCSV,
} from "../../util/bid/NonContractBidUtils";

const BalancesImport = (props) => {
  const [balancesCSVErrors, setBalancesCSVErrors] = useState(null);
  const [showBalanceImportSuccessDialog, setShowBalanceImportSuccessDialog] = useState(false);

  const handleBalancesImport = (event) => {
    try {
      const file = event.target.files[0];

      //workaround to select same file again.
      event.target.value = "";

      if (!file) return;
      if (!file.name.endsWith(".csv")) {
        setBalancesCSVErrors(["Invalid file type. Please upload a CSV file."]);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const csvText = e.target.result;
        if (props.bid.bidType === BID_TYPE.FTO) {
          processIAMFTOBalancesCSV(csvText);
        } else if (props.bid.bidType === BID_TYPE.TWU_DAT_FTO) {
          processTWUDATFTOBalancesCSV(csvText);
        } else if (props.bid.bidType === BID_TYPE.IAM_DAT) {
          processIAMDATBalancesCSV(csvText);
        } else if (props.bid.bidType === BID_TYPE.NC_PTO_FTO) {
          processNCPTOFTOBalancesCSV(csvText);
        } else if (props.bid.bidType === BID_TYPE.NC_PTO_GOC_HOL) {
          processNCPTOGOCHOLBalancesCSV(csvText);
        } else {
          console.error("Invalid Bid Type");
        }
      };
      reader.readAsText(file);
    } catch {
      if (props.bid.bidType === BID_TYPE.FTO) {
        setBalancesCSVErrors(IAM_FTO_BALANCES_IMPORT_ERROR_MESSAGE);
      } else if (props.bid.bidType === BID_TYPE.TWU_DAT_FTO) {
        setBalancesCSVErrors(TWU_DAT_FTO_BALANCES_IMPORT_ERROR_MESSAGE);
      } else if (props.bid.bidType === BID_TYPE.IAM_DAT) {
        setBalancesCSVErrors(IAM_DAT_BALANCES_IMPORT_ERROR_MESSAGE);
      } else if (props.bid.bidType === BID_TYPE.NC_PTO_FTO) {
        setBalancesCSVErrors(NC_PTO_FTO_BALANCES_IMPORT_ERROR_MESSAGE);
      } else if (props.bid.bidType === BID_TYPE.NC_PTO_GOC_HOL) {
        setBalancesCSVErrors(NC_PTO_GOC_HOL_BALANCES_IMPORT_ERROR_MESSAGE);
      } else {
        setBalancesCSVErrors("Error during Balances import");
      }
    }
  };

  const processIAMFTOBalancesCSV = (csvText) => {
    const { data, error } = parseBalancesCSV(csvText);

    if (error) {
      setBalancesCSVErrors([error]);
      return;
    }

    const errors = validateBalancesCSV(data, props.bidGroup);
    if (errors.length) {
      setBalancesCSVErrors(errors);
      return;
    }

    const eidToBalanceMap = {};
    data.forEach((row) => {
      eidToBalanceMap[row["eid"]] = row["fto balance"];
    });

    // update bidgroup with balances
    const updatedBidGroup = [];
    for (const eachEmp of props.bidGroup) {
      const balance = eidToBalanceMap[eachEmp.eid];
      if (balance) {
        updatedBidGroup.push({
          ...eachEmp,
          balance,
        });
      } else {
        updatedBidGroup.push({ ...eachEmp });
      }
    }
    props.setBidGroup(updatedBidGroup);
    setShowBalanceImportSuccessDialog(true);
  };

  const processTWUDATFTOBalancesCSV = (csvText) => {
    const { data, error } = parseTWUDATBalancesCSV(csvText);

    if (error) {
      setBalancesCSVErrors([error]);
      return;
    }

    const errors = validateTWUDATBalancesCSV(data, props.bidGroup);
    if (errors.length) {
      setBalancesCSVErrors(errors);
      return;
    }

    const eidToBalanceMap = Object.fromEntries(
      data.map((row) => [row["eid"], { ftoBalance: row["fto balance"], datBalance: row["dat balance"] }])
    );

    const updatedBidGroup = props.bidGroup.map((eachEmp) => ({
      ...eachEmp,
      ...(eidToBalanceMap[eachEmp.eid] || {}),
    }));

    props.setBidGroup(updatedBidGroup);
    setShowBalanceImportSuccessDialog(true);
  };

  const processIAMDATBalancesCSV = (csvText) => {
    const { data, error } = parseIAMDATBalancesCSV(csvText);

    if (error) {
      setBalancesCSVErrors([error]);
      return;
    }

    const errors = validateIAMDATBalancesCSV(data, props.bidGroup);
    if (errors.length) {
      setBalancesCSVErrors(errors);
      return;
    }

    const eidToBalanceMap = Object.fromEntries(data.map((row) => [row["eid"], { datBalance: row["dat balance"] }]));

    const updatedBidGroup = props.bidGroup.map((eachEmp) => ({
      ...eachEmp,
      ...(eidToBalanceMap[eachEmp.eid] || {}),
    }));

    props.setBidGroup(updatedBidGroup);
    setShowBalanceImportSuccessDialog(true);
  };

  const processNCPTOFTOBalancesCSV = (csvText) => {
    const { data, error } = parseNCPTOFTOBalancesCSV(csvText);

    if (error) {
      setBalancesCSVErrors([error]);
      return;
    }

    const errors = validateNCPTOFTOBalancesCSV(data, props.bidGroup);
    if (errors.length) {
      setBalancesCSVErrors(errors);
      return;
    }

    const eidToBalanceMap = Object.fromEntries(
      data.map((row) => [row["eid"], { ptoBalance: row["pto balance"], ftoBalance: row["fto balance"] }])
    );

    const updatedBidGroup = props.bidGroup.map((eachEmp) => ({
      ...eachEmp,
      ...(eidToBalanceMap[eachEmp.eid] || {}),
    }));

    props.setBidGroup(updatedBidGroup);
    setShowBalanceImportSuccessDialog(true);
  };

  const processNCPTOGOCHOLBalancesCSV = (csvText) => {
    const { data, error } = parseNCPTOGOCHOLBalancesCSV(csvText);

    if (error) {
      setBalancesCSVErrors([error]);
      return;
    }

    const errors = validateNCPTOGOCHOLBalancesCSV(data, props.bidGroup);
    if (errors.length) {
      setBalancesCSVErrors(errors);
      return;
    }

    const eidToBalanceMap = Object.fromEntries(
      data.map((row) => [row["eid"], { ptoBalance: row["pto balance"], gocHolBalance: row["goc-hol balance"] }])
    );

    const updatedBidGroup = props.bidGroup.map((eachEmp) => ({
      ...eachEmp,
      ...(eidToBalanceMap[eachEmp.eid] || {}),
    }));

    props.setBidGroup(updatedBidGroup);
    setShowBalanceImportSuccessDialog(true);
  };

  const getToolTipTitle = () => {
    if (props.bid.bidType === BID_TYPE.FTO) {
      return "Import a CSV of the projected # of FTOs that Employees are able to be awarded in this bid";
    } else if (props.bid.bidType === BID_TYPE.TWU_DAT_FTO) {
      return "Import a CSV of the projected # of DATs and FTOs Employees can be awarded in this bid";
    } else if (props.bid.bidType === BID_TYPE.IAM_DAT) {
      return "Import a CSV of the projected # of DATs Employees can be awarded in this bid";
    } else if (props.bid.bidType === BID_TYPE.NC_PTO_FTO) {
      return "Import a CSV of the projected # of PTOs and FTOs Employees can be awarded in this bid";
    } else if (props.bid.bidType === BID_TYPE.NC_PTO_GOC_HOL) {
      return "Import a CSV of the projected # of PTOs and GOC-HOLs Employees can be awarded in this bid";
    } else return "";
  };

  return (
    <>
      <Tooltip title={getToolTipTitle()} placement="bottom" arrow>
        <Button
          data-testid="bidgroup-import-balance-button"
          variant="outlined"
          color="primary"
          sx={{ marginRight: "15px", height: "40px" }}
          disabled={!props.bidGroup.length || isAwardedBid(props.bid.status) || isDeletedBid(props.bid.status)}
          startIcon={<DownloadingIcon />}
          component="label"
        >
          IMPORT BALANCES
          <input type="file" accept=".csv" hidden onChange={handleBalancesImport} />
        </Button>
      </Tooltip>
      <WarningDialog
        isDialogOpen={!!balancesCSVErrors}
        handleClickDialogClose={() => setBalancesCSVErrors(null)}
        header="Unable to Import"
        message={balancesCSVErrors?.join("\n") || "default message"}
        severity={"error"}
        width="auto"
      />
      <SuccessDialog
        isSuccessDialogOpen={showBalanceImportSuccessDialog}
        title={"Success"}
        message={"Balances imported successfully!"}
        handleOKClick={() => setShowBalanceImportSuccessDialog(false)}
      />
    </>
  );
};

BalancesImport.propTypes = {
  bidGroup: PropTypes.array.isRequired,
  bid: PropTypes.object.isRequired,
  setBidGroup: PropTypes.func.isRequired,
};

export default BalancesImport;
